import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import BeatLoader from 'react-spinners/BeatLoader'

import {
  IconFilterOutline,
  IconPlus,
  IconShareCircles,
  IconSortAsc
} from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import NewStream from 'components/UI/Modal/NewStream/NewStream'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import DropDownSelect from 'features/DropdownSelectNew/DropDownSelect'
import StreamArchiveVideoModal from 'features/StreamsArchive/FullscreenStreamArchive/FullscreenStreamArchive'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
  type StreamArchiveItem,
  deleteStreamArchives,
  fetchStreamArchives
} from 'services/endpoints/streaming'
import { useStreamsArchiveCacheStore } from 'store/streamsArchiveCacheStore'

import { StreamsArchiveCard } from './StreamArchiveCard'
import styles from './StreamsArchive.module.scss'

type SortMode = 'newest' | 'oldest' | 'longest' | 'shortest'

export function StreamsArchive() {
  const { setTitle } = usePageTitle()
  const { t } = useTranslation()
  const modalData = useModalContext()

  const archives = useStreamsArchiveCacheStore((state) => state.archives)
  const pagination = useStreamsArchiveCacheStore((state) => state.pagination)
  const setCachedArchives = useStreamsArchiveCacheStore((state) => state.setArchives)

  const [persistHydrated, setPersistHydrated] = useState(
    useStreamsArchiveCacheStore.persist.hasHydrated()
  )

  const [selectedArchiveIds, setSelectedArchiveIds] = useState<string[]>([])
  const [optionsOpen, setOptionsOpen] = useState(false)

  const [search, setSearch] = useState<string>('')
  const [selectedOption, setSelectedOption] = useState<string>('live')
  const [loading, setLoading] = useState<boolean>(archives.length === 0)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [sortMode, setSortMode] = useState<SortMode>('newest')

  const [previewOpen, setPreviewOpen] = useState(false)
  const [activeArchive, setActiveArchive] = useState<StreamArchiveItem | null>(null)

  const didInitialFetchRef = useRef(false)

  const page = pagination?.current_page ?? 1
  const lastPage = pagination?.last_page ?? 1
  const hasContent = archives.length > 0

  const toggleArchiveSelection = (streamId: string) => {
    setSelectedArchiveIds(prev => {
      const next = prev.includes(streamId)
        ? prev.filter(id => id !== streamId)
        : [...prev, streamId]

      setOptionsOpen(next.length > 0)

      return next
    })
  }

  useEffect(() => {
    const unsubHydrate = useStreamsArchiveCacheStore.persist.onHydrate(() => {
      setPersistHydrated(false)
    })

    const unsubFinish = useStreamsArchiveCacheStore.persist.onFinishHydration(() => {
      setPersistHydrated(true)
    })

    setPersistHydrated(useStreamsArchiveCacheStore.persist.hasHydrated())

    return () => {
      unsubHydrate()
      unsubFinish()
    }
  }, [])

  const options = [
    { value: 'post', label: t('scheduleOptionsPosts') },
    { value: 'premium', label: t('scheduleOptionsPremium') },
    { value: 'message', label: t('scheduleOptionsMessages') },
    { value: 'story', label: t('scheduleOptionsStories') },
    { value: 'live', label: t('scheduleOptionsStreams') }
  ]

  const handleDeleteSelected = async () => {
    if (!selectedArchiveIds.length) return

    await deleteStreamArchives(selectedArchiveIds)

    const nextArchives = archives.filter(
      archive =>
        archive.stream_id == null ||
        !selectedArchiveIds.includes(archive.stream_id)
    )

    if (pagination) {
      setCachedArchives(
        nextArchives,
        {
          ...pagination,
          total: Math.max(
            0,
            pagination.total - selectedArchiveIds.length
          )
        },
        false
      )
    }

    setSelectedArchiveIds([])
    setOptionsOpen(false)
  }

  const openNewStreamModal = () => {
    modalData.addModal('Schedule', <NewStream date={new Date()} />, false)
  }

  const openArchiveVideoModal = (archive: StreamArchiveItem) => {
    const videoUrl =
      archive.playback_url ??
      archive.playlist_key ??
      ''

    if (!videoUrl) return

    setActiveArchive(archive)
    setPreviewOpen(true)
  }

  const closeArchiveVideoModal = () => {
    setPreviewOpen(false)
    setActiveArchive(null)
  }

  const loadArchives = async (
    nextPage = 1,
    append = false,
    silent = false
  ) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else if (!silent && !hasContent) {
        setLoading(true)
      }

      setError('')

      const response = await fetchStreamArchives({
        page: nextPage,
        per_page: 24
      })

      const incoming = response.archives ?? []
      const incomingPagination = response.pagination ?? {
        current_page: nextPage,
        per_page: 24,
        total: incoming.length,
        last_page: 1
      }

      setCachedArchives(incoming, incomingPagination, append)
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load stream archives'
      )
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    setTitle(t('liveStreams'))
  }, [setTitle, t])

  useEffect(() => {
    if (!persistHydrated || didInitialFetchRef.current) return

    didInitialFetchRef.current = true

    const storeState = useStreamsArchiveCacheStore.getState()
    const storeHasContent = storeState.archives.length > 0

    loadArchives(1, false, storeHasContent)
  }, [persistHydrated])

  const filteredAndSortedArchives = useMemo(() => {
    let items = [...archives]

    if (search.trim()) {
      const q = search.trim().toLowerCase()

      items = items.filter((item) => {
        const started = item.recording_started_at
          ? new Date(item.recording_started_at)
          : null

        const dateText = started
          ? started.toLocaleDateString().toLowerCase()
          : ''

        return (
          item.recording_status?.toLowerCase().includes(q) ??
          item.s3_prefix?.toLowerCase().includes(q) ??
          item.playlist_key?.toLowerCase().includes(q) ??
          dateText.includes(q)
        )
      })
    }

    items.sort((a, b) => {
      if (sortMode === 'longest') {
        return (b.duration_ms ?? 0) - (a.duration_ms ?? 0)
      }

      if (sortMode === 'shortest') {
        return (a.duration_ms ?? 0) - (b.duration_ms ?? 0)
      }

      const aTime = a.recording_started_at
        ? new Date(a.recording_started_at).getTime()
        : 0

      const bTime = b.recording_started_at
        ? new Date(b.recording_started_at).getTime()
        : 0

      if (sortMode === 'oldest') {
        return aTime - bTime
      }

      return bTime - aTime
    })

    return items
  }, [archives, search, sortMode])

  const handleSortClick = () => {
    setSortMode((prev) => {
      if (prev === 'newest') return 'oldest'
      if (prev === 'oldest') return 'longest'
      if (prev === 'longest') return 'shortest'
      return 'newest'
    })
  }

  const handleLoadMore = () => {
    if (loadingMore || page >= lastPage) return
    loadArchives(page + 1, true, true)
  }

  const activeVideoUrl =
    activeArchive?.playback_url ??
    activeArchive?.playlist_key ??
    ''

  const activeTitle = activeArchive?.recording_started_at
    ? new Date(activeArchive.recording_started_at).toLocaleString()
    : t('liveStreams')

  const activePoster = activeArchive?.thumbnail_url ?? undefined

  return (
    <div className='streamsarchive'>
      <WithHeaderSection
        headerSection={
          <div className={styles.headerActions}>
            <div className={styles.dropdownWrap}>
              <DropDownSelect
                customClass='streamsArchiveDropdown'
                icon={
                  <div className='analytics-dropdown-icon'>
                    <IconShareCircles />
                  </div>
                }
                placeholder={t('allShares')}
                options={options}
                search={search}
                setSearch={setSearch}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
              />
            </div>

            <div className={styles.buttonsWrap}>
              <IconButton
                icon={<IconSortAsc color='white' />}
                type='black'
                clickFn={handleSortClick}
              />

{/*               <IconButton
                icon={<IconFilterOutline color='white' />}
                type='black'
              /> */}

              <IconButton
                icon={<IconPlus />}
                type='white'
                clickFn={openNewStreamModal}
              />
            </div>
          </div>
        }
        customClass='stream-archive-whs'
      >
        {hasContent ? (
          <>
            <div className={styles.container}>
              {filteredAndSortedArchives.map((archive, index) => (
                <StreamsArchiveCard
                  key={`${archive.metadata_key ?? archive.s3_prefix ?? archive.stream_id ?? 'archive'}-${index}`}
                  selected={
                    archive.stream_id != null &&
                    selectedArchiveIds.includes(archive.stream_id)
                  }
                  onSelect={() => {
                    if (archive.stream_id == null) return
                    toggleArchiveSelection(archive.stream_id)
                  }}
                  onClick={() => openArchiveVideoModal(archive)}
                  streamInfo={{
                    image: archive.thumbnail_url ?? '',
                    startedAt: archive.recording_started_at,
                    durationMs: archive.duration_ms,
                    profit: undefined,
                    videoUrl:
                      archive.playback_url ??
                      archive.playlist_key ??
                      null
                  }}
                />
              ))}
            </div>

            {page < lastPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '20px'
                }}
              >
                {loadingMore ? (
                  <BeatLoader size={8} />
                ) : (
                  <button onClick={handleLoadMore}>
                    {t('loadMore') || 'Load more'}
                  </button>
                )}
              </div>
            )}

            {!!error && (
              <div style={{ padding: '12px 24px' }}>{error}</div>
            )}
          </>
        ) : loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '2rem 0'
            }}
          >
            <BeatLoader size={8} />
          </div>
        ) : error ? (
          <div style={{ padding: '24px' }}>{error}</div>
        ) : (
          <div style={{ padding: '24px' }}>
            {t('noStreamsFound') || 'No archived streams yet'}
          </div>
        )}
      </WithHeaderSection>

    {optionsOpen && selectedArchiveIds.length > 0 && (
      <OptionsDropdown
        role='owner'
        content='stream'
        postId=''
        isOptionsNavOpen={optionsOpen}
        setIsOptionsNavOpen={setOptionsOpen}
        options={null}
        deleteFn={handleDeleteSelected}
        withoutBottomNavigation
      />
    )}

      {previewOpen && activeArchive && activeVideoUrl && (
        <StreamArchiveVideoModal
          videoUrl={activeVideoUrl}
          poster={activePoster}
          title={activeTitle}
          onClose={closeArchiveVideoModal}
        />
      )}
    </div>
  )
}